import { defineStore } from 'pinia';
import { supabase } from '../supabase';
import { setUserId } from '@/core/analytics/analyticsService';
import { useSubscriptionStore } from './useSubscriptionStore';
import { resetBillingNoticeSession } from '@/core/billing/billingNoticeManager';

export const useUserStore = defineStore('user', {
    state: () => ({
        userInfo: {
            id: null,
            email: '',
            role: null,
            avatarUrl: null,
            fullName: null,
            provider: null,
            stripeCustomerId: null
        },
        isLoggedIn: false,
        loginLoading: false,
        loginError: null
    }),

    getters: {
        getUserInfo: (state) => state.userInfo,
        getLoginStatus: (state) => state.isLoggedIn,
    },

    actions: {
        // 邮箱密码登录
        async login(email, password) {
            this.loginLoading = true;
            this.loginError = null;

            try {
                const { data, error } = await supabase.auth.signInWithPassword({
                    email,
                    password,
                });

                if (error) throw error;

                this.userInfo = {
                    id: data.user.id,
                    email: data.user.email,
                    role: data.user.role,
                    avatarUrl: data.user.user_metadata?.avatar_url,
                    stripeCustomerId: data.user.user_metadata?.stripe_customer_id
                };
                this.isLoggedIn = true;

                // 设置 Google Analytics 用户 ID
                setUserId(data.user.id);

                // 后续添加订阅状态检查
                // const subscriptionStore = useSubscriptionStore();
                // await subscriptionStore.checkSubscription();

                return { success: true };
            } catch (error) {
                this.loginError = error.message;
                return {
                    success: false,
                    error: error.message
                };
            } finally {
                this.loginLoading = false;
            }
        },

        // 注册
        async register(email, password) {
            this.loginLoading = true;
            this.loginError = null;

            try {
                const { data, error } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                        emailRedirectTo: `${window.location.origin}/#/confirm`,
                        data: {
                            redirect_after_confirmation: '/'
                        }
                    }
                });

                if (error) throw error;

                // 检查注册状态
                if (data?.user && !data?.session) {
                    // 注册成功但需要邮箱确认
                    return {
                        success: true,
                        needsEmailConfirmation: true
                    };
                } else if (data?.user && data?.session) {
                    // 某些配置下可能直接创建session（如禁用了邮箱确认）
                    this.userInfo = {
                        id: data.user.id,
                        email: data.user.email,
                        role: data.user.role,
                        avatarUrl: data.user.user_metadata?.avatar_url,
                        stripeCustomerId: data.user.user_metadata?.stripe_customer_id
                    };
                    this.isLoggedIn = true;

                    // 设置 Google Analytics 用户 ID
                    setUserId(data.user.id);

                    // 后续初始化订阅状态
                    // const subscriptionStore = useSubscriptionStore();
                    // await subscriptionStore.checkSubscription();

                    return { success: true };
                }

                throw new Error('Registration failed');
            } catch (error) {
                this.loginError = error.message;
                return {
                    success: false,
                    error: error.message
                };
            } finally {
                this.loginLoading = false;
            }
        },

        // 登出
        async logout() {
            let serverError = null;
            try {
                // scope: 'local' 仅吊销当前会话，避免一个环境登出时全局吊销其他环境（dev/beta 共用同一 Supabase 项目）的会话
                const { error } = await supabase.auth.signOut({ scope: 'local' });
                if (error) serverError = error;
            } catch (error) {
                // 网络异常等：仍要本地清理，不阻塞用户登出
                serverError = error;
            } finally {
                // 无论服务端登出是否成功，本地一律清理，解决「点了退出登录没反应」
                // 服务端返回 session_not_found / 403 时，会话其实已不存在，本地清理即等效登出
                this.resetState();
                try {
                    localStorage.removeItem('photoscope-auth');
                } catch (storageError) {
                    // localStorage 不可用时忽略，resetState 已清内存态
                }
                // 重置订阅状态
                const subscriptionStore = useSubscriptionStore();
                subscriptionStore.resetState();
                // 重置计费提醒会话级标志，避免换账号登录（无 reload）时残留误抑新用户首次发放弹窗
                resetBillingNoticeSession();
            }

            // 服务端明确「会话已不存在」视为已登出成功：
            // - supabase 会把 code=session_not_found 的 403 转成 AuthSessionMissingError（name 为 'AuthSessionMissingError'）
            // - 也可能以原始 AuthApiError 形态返回（code=session_not_found / status=403/401）
            const sessionAlreadyGone =
                serverError &&
                (serverError.name === 'AuthSessionMissingError' ||
                    serverError.code === 'session_not_found' ||
                    serverError.status === 403 ||
                    serverError.status === 401 ||
                    (serverError.message &&
                        /session[ _]?not[ _]?found/i.test(String(serverError.message))));

            if (sessionAlreadyGone) {
                // 会话本就不在服务端，本地清理即等效登出 → 视为成功，UserMenu 才会导航到 login
                return { success: true };
            }
            if (serverError) {
                // 真正的服务端/网络错误：本地已清理，但返回失败供上层决定是否提示
                return { success: false, error: serverError.message || String(serverError) };
            }
            return { success: true };
        },

        // 检查当前登录状态
        async checkAuth() {
            try {
                // 首先检查本地存储的会话
                const localSession = JSON.parse(localStorage.getItem('photoscope-auth') || 'null');

                if (localSession?.user) {
                    // 如果有本地会话，立即更新状态
                    this.userInfo = {
                        id: localSession.user.id,
                        email: localSession.user.email,
                        role: localSession.user.role,
                        avatarUrl: localSession.user.user_metadata?.avatar_url,
                        stripeCustomerId: localSession.user.user_metadata?.stripe_customer_id
                    };
                    this.isLoggedIn = true;
                }

                // 然后异步验证会话有效性
                const { data: { session } } = await supabase.auth.getSession();

                if (session?.user) {
                    this.userInfo = {
                        id: session.user.id,
                        email: session.user.email,
                        role: session.user.role,
                        avatarUrl: session.user.user_metadata?.avatar_url,
                        stripeCustomerId: session.user.user_metadata?.stripe_customer_id
                    };
                    this.isLoggedIn = true;

                    // 后续检查订阅状态
                    // const subscriptionStore = useSubscriptionStore();
                    // await subscriptionStore.checkSubscription();
                } else {
                    this.resetState();
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                this.resetState();
            }
        },

        // 重置状态
        resetState() {
            this.userInfo = {
                id: null,
                email: '',
                role: null,
                avatarUrl: null,
                stripeCustomerId: null
            };
            this.isLoggedIn = false;
            this.loginError = null;
        },

        // 更新用户信息
        setUserInfo(userInfo) {
            this.userInfo = { ...this.userInfo, ...userInfo };
        },

        // 更新用户会话
        async setUserSession(session) {
            if (!session?.user?.id) {
                throw new Error('Invalid auth session');
            }

            this.userInfo = {
                id: session.user.id,
                email: session.user.email,
                role: session.user.role,
                avatarUrl: session.user.user_metadata?.avatar_url || null,
                fullName: session.user.user_metadata?.full_name || null,
                provider: session.user.app_metadata?.provider || null,
                stripeCustomerId: session.user.user_metadata?.stripe_customer_id || null
            };
            this.isLoggedIn = true;

            // 设置 Google Analytics 用户 ID
            setUserId(session.user.id);

            // 后续检查并更新订阅状态
            // const subscriptionStore = useSubscriptionStore();
            // await subscriptionStore.checkSubscription();
        },

        // 清除错误信息
        clearError() {
            this.loginError = null;
        },

        // 添加监听认证状态变化的方法
        setupAuthListener() {
            // 保持同步、短小：回调内无 await，去掉 async 符合 Supabase 对 auth callback 的建议（AUTH-01）
            const { data } = supabase.auth.onAuthStateChange((event, session) => {
                if (event === 'SIGNED_IN' && session) {
                    this.userInfo = {
                        id: session.user.id,
                        email: session.user.email,
                        role: session.user.role,
                        avatarUrl: session.user.user_metadata?.avatar_url,
                        stripeCustomerId: session.user.user_metadata?.stripe_customer_id
                    };
                    this.isLoggedIn = true;

                    // 后续检查订阅状态
                    // const subscriptionStore = useSubscriptionStore();
                    // await subscriptionStore.checkSubscription();
                } else if (event === 'SIGNED_OUT') {
                    this.resetState();
                    // 重置订阅状态
                    const subscriptionStore = useSubscriptionStore();
                    subscriptionStore.resetState();
                    // 重置计费提醒会话级标志，避免换账号登录（无 reload）时残留误抑新用户首次发放弹窗
                    resetBillingNoticeSession();
                }
            });
            // 返回 unsubscribe 句柄，交由调用方持有生命周期（方案 §4.8/D6）
            return data?.subscription?.unsubscribe;
        }
    }
}); 
